import React from "react";
import FieldSelector from "./components/FieldSelector";
import FieldEditor from "./components/FieldEditor";

/**
 * Field Set Plugin
 * Sets a field value for selected content items
 */
export default {
  id: "cms-field-set",
  name: "Set Field Value",
  description: "Set a field value for selected items",
  category: "field-operations",

  requiredCapabilities: {
    needsContentType: true,
    needsSelection: true,
    needsFieldAccess: true,
    needsTypeModification: false,
  },

  validate(context) {
    const errors = [];

    if (!context.contentType) {
      errors.push({ message: "Content type is required" });
    }

    if (!context.selectedItems || context.selectedItems.length === 0) {
      errors.push({ message: "Please select items to modify" });
    }

    return {
      valid: errors.length === 0,
      errors: errors.length > 0 ? errors : undefined,
    };
  },

  getInterface(context) {
    const fields = context.typeUtils.getFieldsWithTypes();

    return {
      dialog: {
        title: "Set Field Value",
        fields: [
          {
            id: "fieldName",
            type: "custom",
            label: "Select Field",
            required: true,
            render: (props) => (
              <FieldSelector
                value={props.value}
                onChange={props.onChange}
                fields={fields}
                multiple={false}
                label="Select Field"
                required={true}
                error={props.error}
              />
            ),
          },
          {
            id: "newValue",
            type: "custom",
            label: "New Value",
            required: true,
            visible: (values) => !!values.fieldName,
            render: (props) => {
              const fieldDef = context.typeUtils.getFieldDefinition(props.values.fieldName);
              // If no field definition found, try to get the field info from the fields list
              const fieldInfo = !fieldDef && props.values.fieldName
                ? fields.find(f => f.name === props.values.fieldName)
                : null;

              return (
                <FieldEditor
                  fieldDefinition={fieldDef || fieldInfo}
                  value={props.value}
                  onChange={props.onChange}
                  label="New Value"
                  error={props.error}
                />
              );
            },
          },
        ],
      },

      summary: {
        beforeExecute: (values, context) => ({
          itemsAffected: context.selectedItems.length,
        }),

        afterExecute: (result) => {
          if (result.success) {
            return `Successfully updated ${result.itemsSucceeded} item(s)`;
          }
          return `Updated ${result.itemsSucceeded} item(s), ${result.itemsFailed} failed`;
        },
      },
    };
  },

  async execute(context, params) {
    const { values } = params;
    const { fieldName, newValue } = values;

    let itemsSucceeded = 0;
    let itemsFailed = 0;
    const errors = [];

    try {
      // Show progress
      context.ui.showProgress(0, context.selectedItems.length, "Updating items...");

      // Process each selected item
      for (let i = 0; i < context.selectedItems.length; i++) {
        const item = context.selectedItems[i];

        try {
          // Determine if this is a content field or root field
          const isContentField = fieldName.startsWith('content.');
          const actualFieldName = isContentField ? fieldName.substring(8) : fieldName;

          // Build update object based on field location
          const updateData = isContentField
            ? {
                _id: item._id,
                content: {
                  ...item.content,
                  [actualFieldName]: newValue,
                },
              }
            : {
                _id: item._id,
                [actualFieldName]: newValue,
              };

          // Update the item
          const result = await context.controller.saveContent(updateData, false);

          if (result?.error) {
            itemsFailed++;
            errors.push({
              itemId: item._id,
              error: result.error,
            });
          } else {
            itemsSucceeded++;
          }
        } catch (error) {
          itemsFailed++;
          errors.push({
            itemId: item._id,
            error: error.message,
          });
        }

        // Update progress
        context.ui.showProgress(i + 1, context.selectedItems.length, "Updating items...");
      }

      return {
        success: itemsFailed === 0,
        itemsProcessed: context.selectedItems.length,
        itemsSucceeded,
        itemsFailed,
        errors: errors.length > 0 ? errors : undefined,
      };
    } catch (error) {
      return {
        success: false,
        itemsProcessed: 0,
        itemsSucceeded: 0,
        itemsFailed: context.selectedItems.length,
        errors: [{ error: error.message }],
      };
    }
  },
};
